import { Overlay, OverlayConfig, OverlayRef } from '@angular/cdk/overlay';
import { ComponentPortal, PortalInjector } from '@angular/cdk/portal';
import { ComponentRef, Injectable, Injector } from '@angular/core';
import { filter, take } from 'rxjs/operators';

import { FullscreenModalConfig } from './fullscreen-modal-config';
import { DEFAULT_CONFIG } from './fullscreen-modal-config-default';
import { FullscreenContainerModalComponent } from './fullscreen-modal-container/fullscreen-container-modal.component';
import { FullscreenModalRef } from './fullscreen-modal-ref';
import { FULLSCREEN_MODAL_CONFIG, FULLSCREEN_MODAL_REF } from './fullscreen-modal-tokens';

@Injectable()
export class FullscreenModalService {
  constructor(private overlay: Overlay, private injector: Injector) {}

  open(component: any, config: FullscreenModalConfig = {}) {
    const dialogConfig = { ...DEFAULT_CONFIG, ...config };

    const overlayRef = this.createOverlay(dialogConfig);

    const dialogRef = new FullscreenModalRef(overlayRef);

    const overlayComponent = this.attachDialogContainer(
      component,
      overlayRef,
      dialogConfig,
      dialogRef
    );

    dialogRef.componentInstance = overlayComponent;
    overlayRef.backdropClick().subscribe(_ => dialogRef.close(null));

    dialogRef.componentInstance.animationStateChanged
      .pipe(
        filter(event => event.phaseName === 'done' && event.toState === 'enter'),
        take(1)
      )
      .subscribe(() => {
        dialogRef._afterOpened.next();
      });

    return dialogRef;
  }

  private getOverLayConfig(config: FullscreenModalConfig): OverlayConfig {
    const positionStrategy = this.overlay
      .position()
      .global()
      .centerHorizontally()
      .centerHorizontally();

    const overlayConfig = new OverlayConfig({
      width: config.width,
      height: config.height,
      hasBackdrop: config.hasBackdrop,
      panelClass: config.panelClass,
      backdropClass: config.backdropClass,
      scrollStrategy: this.overlay.scrollStrategies.block(),
      positionStrategy
    });

    return overlayConfig;
  }

  private createOverlay(config: FullscreenModalConfig) {
    const overlayConfig = this.getOverLayConfig(config);
    return this.overlay.create(overlayConfig);
  }

  private createInjector(
    component: any,
    config: FullscreenModalConfig,
    dialogRef: FullscreenModalRef
  ): PortalInjector {
    const injectionTokens = new WeakMap();
    injectionTokens.set(FullscreenModalRef, dialogRef);
    injectionTokens.set(FULLSCREEN_MODAL_CONFIG, config);
    injectionTokens.set(FULLSCREEN_MODAL_REF, component);

    return new PortalInjector(this.injector, injectionTokens);
  }

  private attachDialogContainer(
    component: any,
    overLayRef: OverlayRef,
    config: FullscreenModalConfig,
    dialogRef: FullscreenModalRef
  ) {
    const injector = this.createInjector(component, config, dialogRef);
    const containerPortal = new ComponentPortal(FullscreenContainerModalComponent, null, injector);
    const containerRef: ComponentRef<FullscreenContainerModalComponent> = overLayRef.attach(
      containerPortal
    );

    return containerRef.instance;
  }
}
